import { API } from "@/lib/api";
import { ApiError, APIService, FetchListRequestParams } from "@/services/api";
import {
  CalculateContractorsPlannedHoursWizardRequest,
  ReadContractorsPlannedHoursWizardAllocation,
  ReadContractorsPlannedHoursWizardResult,
} from "@/types/contractorsPlannedHoursWizard";

const contractorsPlannedHoursWizardService =
  new APIService<ReadContractorsPlannedHoursWizardAllocation>(
    API.ContractorsPlannedHoursWizard,
  );

function normalizeCalculateWizardRow(
  row: unknown,
): ReadContractorsPlannedHoursWizardResult | null {
  if (!row || typeof row !== "object") return null;
  const rowRecord = row as Record<string, unknown>;
  const period = rowRecord.period;
  const hoursRaw = rowRecord.hours ?? rowRecord.houts;
  if (typeof period !== "string") return null;
  if (typeof hoursRaw !== "string" && typeof hoursRaw !== "number") return null;
  return { period, hours: String(hoursRaw) };
}

function parseCalculateWizardResponse(
  json: unknown,
): ReadContractorsPlannedHoursWizardResult[] {
  if (Array.isArray(json)) {
    return json
      .map(normalizeCalculateWizardRow)
      .filter((row): row is ReadContractorsPlannedHoursWizardResult => !!row);
  }
  if (
    json &&
    typeof json === "object" &&
    "data" in json &&
    Array.isArray((json as { data: unknown }).data)
  ) {
    return (json as { data: unknown[] }).data
      .map(normalizeCalculateWizardRow)
      .filter((row): row is ReadContractorsPlannedHoursWizardResult => !!row);
  }
  return [];
}

export const ContractorsPlannedHoursWizardService = {
  getList: (
    fetchParams: FetchListRequestParams<ReadContractorsPlannedHoursWizardAllocation>,
  ) => contractorsPlannedHoursWizardService.getList(fetchParams),
  calculate: async (
    data: CalculateContractorsPlannedHoursWizardRequest,
  ): Promise<ReadContractorsPlannedHoursWizardResult[]> => {
    const service = contractorsPlannedHoursWizardService;
    const response = await service.performFetch(service.path, "POST", data);
    await service.handleResponseError(response);
    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw new ApiError("Invalid JSON response", response.status);
    }
    return parseCalculateWizardResponse(json);
  },
};
